import { z } from 'zod';
import { uuidIdSchema } from './ids.js';
export const CAREER_TIERS = {
    rookie: 0,
    scout: 500,
    punter: 2000,
    gaffer: 5000,
    mastermind: 10000,
    legend: 20000,
};
export const careerTierIdSchema = z.enum(['rookie', 'scout', 'punter', 'gaffer', 'mastermind', 'legend']);
export const trophyIdSchema = z.enum([
    'crowd_slayer',
    'captain_assassin',
    'iron_manager',
    'perfect_week',
    'last_believer',
    'giant_leap',
    'early_bird',
]);
export const TROPHY_TIER_IDS = ['bronze', 'silver', 'gold', 'platinum', 'legendary', 'mythic'];
export const trophyTierSchema = z.enum(TROPHY_TIER_IDS);
export const trophyTierIndexSchema = z.number().int().min(0).max(5);
export const trophyKindSchema = z.enum(['multi_tier', 'one_shot']);
const trophyCopyKeys = (id) => ({
    titleKey: `gamification.trophy.${id}.title`,
    descriptionKey: `gamification.trophy.${id}.description`,
    conditionKey: `gamification.trophy.${id}.condition`,
});
const MULTI_TIER_TIER_IDS = TROPHY_TIER_IDS;
export const TROPHY_CATALOG = deepFreeze({
    crowd_slayer: {
        id: 'crowd_slayer',
        kind: 'multi_tier',
        metric: 'cumulative_qualifying_wins',
        ...trophyCopyKeys('crowd_slayer'),
        thresholds: [3, 10, 25, 50, 100, 200],
        tierIds: MULTI_TIER_TIER_IDS,
    },
    captain_assassin: {
        id: 'captain_assassin',
        kind: 'multi_tier',
        metric: 'best_consecutive_captain_success_streak',
        ...trophyCopyKeys('captain_assassin'),
        thresholds: [3, 5, 8, 10, 12, 15],
        tierIds: MULTI_TIER_TIER_IDS,
    },
    iron_manager: {
        id: 'iron_manager',
        kind: 'multi_tier',
        metric: 'best_consecutive_active_gameweek_streak',
        ...trophyCopyKeys('iron_manager'),
        thresholds: [3, 5, 10, 15, 20, 38],
        tierIds: MULTI_TIER_TIER_IDS,
    },
    perfect_week: {
        id: 'perfect_week',
        kind: 'one_shot',
        metric: 'perfect_week_unlock',
        ...trophyCopyKeys('perfect_week'),
        thresholds: null,
        tierIds: null,
    },
    last_believer: {
        id: 'last_believer',
        kind: 'one_shot',
        metric: 'last_believer_unlock',
        ...trophyCopyKeys('last_believer'),
        thresholds: null,
        tierIds: null,
    },
    giant_leap: {
        id: 'giant_leap',
        kind: 'multi_tier',
        metric: 'max_net_gameweek_rating_delta',
        ...trophyCopyKeys('giant_leap'),
        thresholds: [30, 50, 100, 150, 250, 350],
        tierIds: MULTI_TIER_TIER_IDS,
    },
    early_bird: {
        id: 'early_bird',
        kind: 'multi_tier',
        metric: 'cumulative_early_correct_calls',
        ...trophyCopyKeys('early_bird'),
        thresholds: [10, 25, 50, 100, 200, 500],
        tierIds: MULTI_TIER_TIER_IDS,
    },
});
export const CURRENT_GAMIFICATION_RULE_VERSION = 'v1';
export const gamificationRuleVersionSchema = z.enum([CURRENT_GAMIFICATION_RULE_VERSION]);
export const GAMIFICATION_XP_RATES_V1 = {
    duel_vote: 1,
    correct_call: 2,
    captain_vote: 5,
    active_gameweek: 10,
};
export const GAMIFICATION_RULE_SETS = deepFreeze({
    v1: {
        version: CURRENT_GAMIFICATION_RULE_VERSION,
        effectiveFrom: null,
        careerTiers: CAREER_TIERS,
        xpRates: GAMIFICATION_XP_RATES_V1,
        trophyCatalog: TROPHY_CATALOG,
        earlyBirdLeadDays: 5,
    },
});
export const GAMIFICATION_RULE_VERSIONS = Object.freeze(Object.keys(GAMIFICATION_RULE_SETS));
export function createGamificationUnsupportedRuleVersionError(version, supportedVersions = GAMIFICATION_RULE_VERSIONS) {
    return {
        kind: 'GAMIFICATION_UNSUPPORTED_RULE_VERSION',
        version,
        supportedVersions,
    };
}
export function resolveGamificationRuleSet(version) {
    const parsedVersion = gamificationRuleVersionSchema.safeParse(version);
    if (!parsedVersion.success) {
        return {
            ok: false,
            error: createGamificationUnsupportedRuleVersionError(version),
        };
    }
    const ruleSet = GAMIFICATION_RULE_SETS[parsedVersion.data];
    if (!ruleSet) {
        return {
            ok: false,
            error: createGamificationUnsupportedRuleVersionError(version),
        };
    }
    return { ok: true, ruleSet };
}
export const gamificationProgressBreakdownLineSchema = z.object({
    count: z.number().int().nonnegative(),
    rate: z.number().int().nonnegative(),
    xp: z.number().int().nonnegative(),
});
export const gamificationProgressBreakdownSchema = z.object({
    duel_votes: gamificationProgressBreakdownLineSchema,
    correct_calls: gamificationProgressBreakdownLineSchema,
    captain_votes: gamificationProgressBreakdownLineSchema,
    active_gameweeks: gamificationProgressBreakdownLineSchema,
});
export const gamificationProgressSchema = z.object({
    xp: z.number().int().nonnegative(),
    tier: careerTierIdSchema,
    next_tier: careerTierIdSchema.nullable(),
    xp_to_next: z.number().int().nonnegative().nullable(),
    segment_progress: z.number().min(0).max(1),
    top_percent: z.number().int().min(1).max(100).nullable(),
    is_active_current_season: z.boolean(),
    breakdown: gamificationProgressBreakdownSchema,
});
export const trophyStatusSchema = z.enum(['locked', 'in_progress', 'unlocked', 'maxed']);
/** Allowlisted trophy evidence keys only (NFR-6: no PII / free-form blobs). */
export const gamificationTrophyEvidenceSchema = z
    .object({
    gameweek_id: uuidIdSchema.optional(),
    gameweek_number: z.number().int().nonnegative().optional(),
    duel_id: uuidIdSchema.optional(),
    duel_slug: z.string().min(1).max(160).optional(),
    winner_player_name: z.string().min(1).max(120).optional(),
    loser_player_name: z.string().min(1).max(120).optional(),
    winner_share: z.number().min(0).max(1).optional(),
    winner_human_votes: z.number().int().nonnegative().optional(),
    total_human_votes: z.number().int().nonnegative().optional(),
})
    .strict()
    .nullable();
/** Strip unknown keys before persisting trophy evidence. */
export function sanitizeGamificationTrophyEvidence(value) {
    if (value == null || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }
    const parsed = gamificationTrophyEvidenceSchema.safeParse(value);
    if (parsed.success) {
        return parsed.data;
    }
    // Keep only allowlisted keys when extra fields are present.
    const record = value;
    const candidate = {
        gameweek_id: record.gameweek_id,
        gameweek_number: record.gameweek_number,
        duel_id: record.duel_id,
        duel_slug: record.duel_slug,
        winner_player_name: record.winner_player_name,
        loser_player_name: record.loser_player_name,
        winner_share: record.winner_share,
        winner_human_votes: record.winner_human_votes,
        total_human_votes: record.total_human_votes,
    };
    const stripped = gamificationTrophyEvidenceSchema.safeParse(candidate);
    return stripped.success ? stripped.data : null;
}
export const gamificationTrophyItemSchema = z.object({
    trophy_id: trophyIdSchema,
    title_key: z.string(),
    description_key: z.string(),
    condition_key: z.string(),
    kind: trophyKindSchema,
    status: trophyStatusSchema,
    current_tier: trophyTierSchema.nullable(),
    current_tier_index: trophyTierIndexSchema.nullable(),
    highest_tier: trophyTierSchema.nullable(),
    highest_tier_index: trophyTierIndexSchema.nullable(),
    metric_value: z.number(),
    next_target: z.number().nullable(),
    next_progress: z.number().min(0).max(1).nullable(),
    rarity_pct: z.number().min(0).max(100).nullable(),
    first_unlocked_at: z.string().datetime().nullable(),
    first_unlocked_gameweek: z.number().int().positive().nullable(),
    /** Gameweek that produced the best run ("Closest: 9/10 (GW18)"); null when not applicable. */
    closest_gameweek: z.number().int().positive().nullable(),
    /** Best run value behind `closest_gameweek` (wins, streak length, rating delta, or 0..1 progress). */
    best_value: z.number(),
    evidence: gamificationTrophyEvidenceSchema,
    is_new: z.boolean(),
});
export const gamificationPinSourceSchema = z.enum(['manual', 'auto']);
export const gamificationPrivatePinnedSchema = z.object({
    trophy_id: trophyIdSchema.nullable(),
    source: gamificationPinSourceSchema.nullable(),
});
export const gamificationPublicPinnedSchema = z.object({
    trophy_id: trophyIdSchema.nullable(),
});
/**
 * Normalized effective pin for public surfaces (profile, leaderboard, voters).
 * Never includes pin_source / manual pin / private events.
 */
export const publicPinnedTrophySchema = z.object({
    trophy_id: trophyIdSchema,
    title_key: z.string().min(1),
    highest_tier: trophyTierSchema.nullable(),
    highest_tier_index: trophyTierIndexSchema.nullable(),
    rarity_pct: z.number().min(0).max(100).nullable(),
});
export const publicPinnedTrophyNullableSchema = publicPinnedTrophySchema.nullable();
export const gamificationEventTypeSchema = z.enum([
    'progress_tier_unlocked',
    'trophy_tier_unlocked',
    'trophy_one_shot_unlocked',
]);
export const gamificationSharePayloadKindSchema = z.enum([
    'progress_tier',
    'trophy_tier',
    'trophy_one_shot',
]);
/**
 * Allowlisted Web Share payload. Writers must emit title/text/url;
 * optional metadata keys are for analytics/debug only and never include PII.
 */
export const gamificationSharePayloadSchema = z.object({
    title: z.string().min(1),
    text: z.string().min(1),
    url: z.string().url(),
    kind: gamificationSharePayloadKindSchema.optional(),
    trophy_id: trophyIdSchema.optional(),
    tier: careerTierIdSchema.optional(),
    tier_index: trophyTierIndexSchema.nullable().optional(),
});
export const gamificationHeroEventSchema = z
    .object({
    id: uuidIdSchema,
    event_type: gamificationEventTypeSchema,
    trophy_id: trophyIdSchema.nullable(),
    tier_index: trophyTierIndexSchema.nullable(),
    title: z.string(),
    body: z.string(),
    share_payload: gamificationSharePayloadSchema.nullable(),
    created_at: z.string().datetime(),
})
    .nullable();
export const gamificationUpdatesSchema = z.object({
    has_unseen: z.boolean(),
    count: z.number().int().nonnegative(),
    latest_event_id: uuidIdSchema.nullable(),
    hero_event: gamificationHeroEventSchema,
});
export const gamificationProcessingSchema = z.object({
    latest_finalized_gameweek: z.number().int().nonnegative().nullable(),
    current_gameweek_pending: z.boolean(),
});
export const gamificationMeResponseSchema = z.object({
    progress: gamificationProgressSchema,
    trophies: z.array(gamificationTrophyItemSchema),
    pinned: gamificationPrivatePinnedSchema,
    updates: gamificationUpdatesSchema,
    processing: gamificationProcessingSchema,
});
export const gamificationPublicResponseSchema = z.object({
    progress: gamificationProgressSchema,
    trophies: z.array(gamificationTrophyItemSchema),
    pinned: gamificationPublicPinnedSchema,
});
// Deliberately loose: an unknown id is resolved against the catalog in the query layer so the
// API answers 404 GAMIFICATION_TROPHY_NOT_FOUND rather than a generic 400.
export const gamificationPinBodySchema = z.object({
    trophy_id: z.string().min(1),
});
export const gamificationPinResponseSchema = gamificationPrivatePinnedSchema.nullable();
export const gamificationEventsSeenBodySchema = z.object({
    through_event_id: uuidIdSchema,
});
function deepFreeze(value) {
    if (value && typeof value === 'object') {
        Object.freeze(value);
        for (const nested of Object.values(value)) {
            deepFreeze(nested);
        }
    }
    return value;
}
